import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast, toastErrorMessage } from "../lib/toast";
import Sheet from "../components/Sheet";
import { TierTooltip } from "../lib/tierLegend";
import { CsvButton, PrintButton } from "../components/ReportButtons";
import { Plus, ArrowUp, ArrowDown } from "lucide-react";

// Values used to sort each column. Kept out of the component body so it
// doesn't rebuild on every render.
const SORT_ACCESSORS = {
  name:     (r, users) => (r.company_name || "").toLowerCase(),
  state:    (r) => r.state || "",
  tier:     (r) => r.customer_tier ?? 999,
  segments: (r) => (r.product_segments || []).join(","),
  drill:    (r) => r.drill_market_segment || "",
  assigned: (r, users) => (
    users.find(u => u.id === (r.assigned_rep_id || r.created_by_id))?.name || ""
  ).toLowerCase(),
};

export default function Companies() {
  const { user, meta } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    state: "", tier: "", segment: "", current_customer: "All",
    owns_equipment: "All", country: "", major_town: "",
    assigned_rep_id: "", q: "",
  });
  const [sort, setSort] = useState({ col: "name", dir: "asc" });

  function toggleSort(col) {
    setSort(prev => prev.col === col
      ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { col, dir: "asc" }
    );
  }

  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const sortedRows = useMemo(() => {
    const accessor = SORT_ACCESSORS[sort.col] || SORT_ACCESSORS.name;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = accessor(a, users);
      const bv = accessor(b, users);
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, users, sort]);

  async function reload() {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      for (const [k, v] of Object.entries(filters)) if (v) p.set(k, v);
      const { data } = await api.get(`/companies?${p}`);
      setRows(data);
    } catch {
      // Reload failure - leave list empty; filter change retries.
      setRows([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, [filters]);
  useEffect(() => {
    api.get("/users").then(r => setUsers(r.data)).catch(() => setUsers([]));
  }, []);

  return (
    <div className="space-y-4" data-testid="companies-page">
      <div className="flex items-center justify-between gap-3 flex-wrap print-hide">
        <div>
          <h1 className="text-2xl font-bold">Companies</h1>
          <p className="text-gray-500 text-sm">{rows.length} result{rows.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <CsvButton path="/reports/companies.csv"
                     params={Object.fromEntries(Object.entries(filters).filter(([,v])=>v && v!=="All"))}
                     testId="companies-csv"/>
          <PrintButton testId="companies-print"/>
          <button className="cesco-btn-primary flex items-center gap-2" onClick={() => setShowNew(true)}
                  data-testid="btn-new-company"><Plus size={16}/> New company</button>
        </div>
      </div>

      <div className="cesco-card p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 print-hide">
        <input className="cesco-input col-span-2 md:col-span-2" placeholder="Search name…"
               value={filters.q} onChange={e=>setFilters({...filters, q: e.target.value})}
               data-testid="filter-q"/>
        <select className="cesco-input" value={filters.state}
                onChange={e=>setFilters({...filters, state: e.target.value})}
                data-testid="filter-state">
          <option value="">All states</option>
          {meta?.au_states.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
        </select>
        <select className="cesco-input" value={filters.tier}
                onChange={e=>setFilters({...filters, tier: e.target.value})}
                data-testid="filter-tier">
          <option value="">All tiers</option>
          {meta?.customer_tiers.map(t => <option key={t.value} value={t.value}>{t.value}</option>)}
        </select>
        <select className="cesco-input" value={filters.segment}
                onChange={e=>setFilters({...filters, segment: e.target.value})}
                data-testid="filter-segment">
          <option value="">All segments</option>
          {meta?.product_segments.map(s => <option key={s.code} value={s.code}>{`${s.code} - ${s.name}`}</option>)}
        </select>
        <select className="cesco-input" value={filters.current_customer}
                onChange={e=>setFilters({...filters, current_customer: e.target.value})}
                data-testid="filter-current-customer">
          <option value="All">Current Customer: all</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
        <select className="cesco-input" value={filters.owns_equipment}
                onChange={e=>setFilters({...filters, owns_equipment: e.target.value})}
                data-testid="filter-owns-equipment">
          <option value="All">Owns Equipment: all</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
        <input className="cesco-input" placeholder="Country" value={filters.country}
               onChange={e=>setFilters({...filters, country: e.target.value})}
               data-testid="filter-country"/>
        <input className="cesco-input" placeholder="Major town" value={filters.major_town}
               onChange={e=>setFilters({...filters, major_town: e.target.value})}
               data-testid="filter-major-town"/>
        {user?.role === "admin" && (
          <select className="cesco-input" value={filters.assigned_rep_id}
                  onChange={e=>setFilters({...filters, assigned_rep_id: e.target.value})}
                  data-testid="filter-rep">
            <option value="">Any rep</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
      </div>

      <div className="cesco-card overflow-hidden print-region">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <SortableTh label="Name" col="name" sort={sort} onSort={toggleSort}/>
                <SortableTh label="State" col="state" sort={sort} onSort={toggleSort}/>
                <SortableTh label="Tier" col="tier" sort={sort} onSort={toggleSort}/>
                <SortableTh label="Segments" col="segments" sort={sort} onSort={toggleSort}/>
                <SortableTh label="Drill Market" col="drill" sort={sort} onSort={toggleSort} hideOnMobile/>
                <SortableTh label="Assigned" col="assigned" sort={sort} onSort={toggleSort} hideOnMobile/>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-4"><div className="skeleton h-6 w-full"/></td></tr>}
              {!loading && sortedRows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-gray-500">No companies yet</td></tr>}
              {sortedRows.map(r => (
                <tr key={r.id}
                    className="border-t hover:bg-[var(--theme-accent-50)] cursor-pointer"
                    onClick={(e)=>{ if (e.target.tagName !== "A") navigate(`/companies/${r.id}`); }}
                    data-testid="company-row">
                  <td className="px-4 py-3">
                    <Link to={`/companies/${r.id}`} className="font-medium hover:underline" data-testid={`link-company-${r.id}`}>
                      {r.company_name || <span className="italic text-gray-400">(Unnamed - click to fix)</span>}
                    </Link>
                    <div className="text-xs text-gray-500">{r.suburb} {r.postcode}</div>
                  </td>
                  <td className="px-4 py-3">{r.state}</td>
                  <td className="px-4 py-3"><span className="pill pill-grey">Tier {r.customer_tier}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(r.product_segments || []).map(s => {
                        const isM = s === "M";
                        return <span key={s} className={`pill ${isM ? "pill-grey" : "pill-green"}`}>{s}</span>;
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-gray-700">
                    {r.drill_market_segment || "-"}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-gray-600">
                    {users.find(u => u.id === (r.assigned_rep_id || r.created_by_id))?.name || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showNew && <NewCompany onClose={() => setShowNew(false)} onCreated={reload} meta={meta} />}
    </div>
  );
}

function NewCompany({ onClose, onCreated, meta }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    company_name:"", state:"", customer_tier:"",
    phone:"", website:"", street:"", suburb:"", postcode:"", major_town:"", area_state:"",
    country:"Australia", generic_email:"",
    current_customer:"", owns_equipment:"", tier_reason:"", type_of_contact:"",
    product_segments: [],
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function toggleSeg(code) {
    set("product_segments", form.product_segments.includes(code)
      ? form.product_segments.filter(s => s !== code)
      : [...form.product_segments, code]);
  }

  async function submit({ thenAddContact = false } = {}) {
    setErr(""); setSaving(true);
    try {
      const name = (form.company_name || "").trim();
      if (!name) {
        const msg = "Company name is required.";
        setErr(msg); toast.error(msg); setSaving(false); return;
      }
      const payload = { ...form, company_name: name, customer_tier: Number(form.customer_tier) };
      for (const k of ["current_customer","owns_equipment","tier_reason","type_of_contact"]) {
        if (!payload[k]) delete payload[k];
      }
      const { data } = await api.post("/companies", payload);
      toast.success(`${name} created`);
      onCreated();
      onClose();
      if (thenAddContact && data?.id) {
        navigate(`/companies/${data.id}?tab=Contacts&new_contact=1`);
      }
    } catch(e) {
      const msg = toastErrorMessage(e);
      setErr(msg); toast.error(msg);
    }
    finally { setSaving(false); }
  }

  function onFormSubmit(e) {
    // Native form submit (Enter key) defaults to plain Create.
    e.preventDefault();
    submit();
  }

  return (
    <Sheet open={true} onClose={onClose} title="New company" widthClass="max-w-2xl"
      footer={
        <div className="flex gap-2 justify-end flex-wrap">
          <button className="cesco-btn-ghost" onClick={onClose} data-testid="new-cancel">Cancel</button>
          <button className="cesco-btn-ghost" onClick={()=>submit({ thenAddContact: true })} disabled={saving}
                  data-testid="new-save-and-contact">
            {saving ? "Saving…" : "Create and add contact"}
          </button>
          <button className="cesco-btn-primary" onClick={()=>submit()} disabled={saving}
                  data-testid="new-save">{saving ? "Saving…" : "Create"}</button>
        </div>
      }>
      <form onSubmit={onFormSubmit} className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="cesco-label">Company name *</label>
          <input className="cesco-input" value={form.company_name} required minLength={1}
                 onChange={e=>set("company_name", e.target.value)} data-testid="new-name"/>
        </div>
        <div>
          <label className="cesco-label">State *</label>
          {(!form.country || form.country === "Australia") ? (
            <select className="cesco-input" value={form.state} required
                    onChange={e=>set("state", e.target.value)} data-testid="new-state">
              <option value="">Select</option>
              {meta?.au_states.map(s => <option key={s.code} value={s.code}>{`${s.code} - ${s.name}`}</option>)}
            </select>
          ) : (
            <input className="cesco-input" value={form.state} required maxLength={40}
                   placeholder="State / region (e.g. Auckland)"
                   onChange={e=>set("state", e.target.value)} data-testid="new-state"/>
          )}
        </div>
        <div>
          <label className="cesco-label flex items-center gap-1.5">
            Customer Tier *
            <TierTooltip testId="new-tier-tooltip"/>
          </label>
          <select className="cesco-input" value={form.customer_tier} required
                  onChange={e=>set("customer_tier", e.target.value)} data-testid="new-tier">
            <option value="">Select</option>
            {meta?.customer_tiers.map(t => <option key={t.value} value={t.value}>{t.value}</option>)}
          </select>
        </div>
        <div>
          <label className="cesco-label">Company Phone Number</label>
          <input className="cesco-input" value={form.phone} onChange={e=>set("phone", e.target.value)} />
        </div>
        <div>
          <label className="cesco-label">Website</label>
          <input className="cesco-input" value={form.website} maxLength={200}
                 onChange={e=>set("website", e.target.value)} />
        </div>
        <div>
          <label className="cesco-label">Street</label>
          <input className="cesco-input" value={form.street} maxLength={80}
                 onChange={e=>set("street", e.target.value)} />
        </div>
        <div>
          <label className="cesco-label">Suburb</label>
          <input className="cesco-input" value={form.suburb} maxLength={60}
                 onChange={e=>set("suburb", e.target.value)} />
        </div>
        <div>
          <label className="cesco-label">Postcode</label>
          <input className="cesco-input" value={form.postcode} maxLength={6}
                 onChange={e=>set("postcode", e.target.value)} />
        </div>
        <div>
          <label className="cesco-label">Country</label>
          <select className="cesco-input" value={form.country}
                  onChange={e=>set("country", e.target.value)}>
            {meta?.countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="cesco-label">Product Segments</label>
          <div className="flex flex-wrap gap-2">
            {meta?.product_segments.map(s => {
              const active = form.product_segments.includes(s.code);
              const isM = s.code === "M";
              const cls = active ? (isM ? "pill pill-grey" : "pill pill-green") : "pill";
              return (
                <button type="button" key={s.code} onClick={()=>toggleSeg(s.code)}
                  className={`${cls} cursor-pointer`}
                  style={!active ? { border: "1px dashed #B6BAC5", color: "#666", background:"#fff" } : {}}
                  data-testid={`new-segment-${s.code}`}>
                  {s.code} - {s.name}
                </button>
              );
            })}
          </div>
        </div>
        {err && <div className="col-span-2 text-sm text-red-600" data-testid="new-error">{err}</div>}
      </form>
    </Sheet>
  );
}


function SortableTh({ label, col, sort, onSort, hideOnMobile }) {
  const active = sort.col === col;
  const asc = sort.dir === "asc";
  return (
    <th className={`text-left px-4 py-3 select-none ${hideOnMobile ? "hidden md:table-cell" : ""}`}>
      <button type="button" onClick={() => onSort(col)}
              className="inline-flex items-center gap-1 hover:text-gray-900"
              data-testid={`sort-${col}`}>
        {label}
        {active
          ? (asc ? <ArrowUp size={12}/> : <ArrowDown size={12}/>)
          : <span className="w-3 opacity-30">↕</span>}
      </button>
    </th>
  );
}
